import { cn } from "@/lib/core/utils";

// Demo shift data — two columns showing realistic multi-employee scheduling
const DEMO_SHIFTS = [
  // Column 0
  { id: "s1", title: "Open", start: 8, end: 14, employee: "JD", status: "completed" as const, col: 0 },
  { id: "s2", title: "Close", start: 15, end: 18, employee: "MK", status: "in_progress" as const, col: 0 },
  // Column 1
  { id: "s3", title: "Manager", start: 9, end: 18, employee: "AS", status: "in_progress" as const, col: 1 },
];

const STATUS_COLORS: Record<string, { bg: string; border: string }> = {
  completed: { bg: "bg-green-500/60", border: "border-green-300/40" },
  in_progress: { bg: "bg-emerald-500/60", border: "border-emerald-300/40" },
  pending: { bg: "bg-blue-500/60", border: "border-blue-300/40" },
};

const START_HOUR = 7;
const END_HOUR = 19;
const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i);
const HOUR_HEIGHT = 28; // Scaled down from real 48px to fit card
const TIME_COL_W = 30; // Scaled from real 42px
const SHIFT_COL_W = 52; // Width per shift column

const OPENING_HOUR = 9;
const CLOSING_HOUR = 18;
const CURRENT_TIME_HOUR = 14.5; // 14:30

const MAX_COLS = 2;

// Mobile Calendar Visualization — vertical time grid matching real MobileTimeGrid
export const CalendarVisualizationMobile = () => {
  const gridHeight = HOURS.length * HOUR_HEIGHT;
  const currentTimeTop = (CURRENT_TIME_HOUR - START_HOUR) * HOUR_HEIGHT;

  return (
    <div className="relative w-full h-full overflow-hidden flex flex-col">
      {/* Day header — matching MobileAgendaDayHeader style */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border/30 shrink-0">
        <span className="text-2xs font-semibold text-foreground/80 tracking-wide">MON</span>
        <span className="text-3xs px-1.5 py-0.5 rounded-full bg-primary/15 text-primary font-medium">
          Today
        </span>
      </div>

      {/* Time grid */}
      <div className="relative flex-1 overflow-hidden">
        <div className="relative flex" style={{ height: gridHeight }}>
          {/* Time labels column — fixed left */}
          <div
            className="shrink-0 border-r border-border/30 bg-background"
            style={{ width: TIME_COL_W }}
          >
            {HOURS.map((hour) => (
              <div
                key={hour}
                className={cn(
                  "flex items-start justify-center pt-0.5 border-t",
                  hour === OPENING_HOUR ? "!border-t-[1.5px] !border-t-primary" : "border-border/40",
                  hour === CLOSING_HOUR - 1 ? "!border-b-[1.5px] !border-b-primary" : "",
                  hour >= OPENING_HOUR && hour < CLOSING_HOUR && "bg-primary/8",
                )}
                style={{ height: HOUR_HEIGHT }}
              >
                <span className="text-3xs font-medium text-muted-foreground/70 tabular-nums leading-none">
                  {hour.toString().padStart(2, "0")}:00
                </span>
              </div>
            ))}
          </div>

          {/* Grid area with shift blocks */}
          <div
            className="relative flex-1"
            style={{ minWidth: MAX_COLS * SHIFT_COL_W }}
          >
            {/* Hour row backgrounds + half-hour lines */}
            {HOURS.map((hour) => (
              <div
                key={hour}
                className={cn(
                  "relative border-t",
                  hour === OPENING_HOUR ? "!border-t-[1.5px] !border-t-primary" : "border-border/40",
                  hour === CLOSING_HOUR - 1 ? "!border-b-[1.5px] !border-b-primary" : "",
                  hour >= OPENING_HOUR && hour < CLOSING_HOUR && "bg-primary/8",
                )}
                style={{ height: HOUR_HEIGHT }}
              >
                {/* Half-hour dashed line */}
                <div
                  className="absolute left-0 right-0 border-t border-dashed border-border/20"
                  style={{ top: HOUR_HEIGHT / 2 }}
                />
              </div>
            ))}

            {/* Shift blocks — absolutely positioned */}
            {DEMO_SHIFTS.map((shift) => {
              const top = (shift.start - START_HOUR) * HOUR_HEIGHT;
              const height = (shift.end - shift.start) * HOUR_HEIGHT;
              const left = shift.col * SHIFT_COL_W + 2;
              const width = SHIFT_COL_W - 4;
              const colors = STATUS_COLORS[shift.status];
              const duration = shift.end - shift.start;

              return (
                <div
                  key={shift.id}
                  className={cn(
                    "absolute rounded-[3px] border overflow-hidden",
                    "flex flex-col p-1",
                    colors.bg,
                    colors.border,
                  )}
                  style={{ top, height, left, width }}
                >
                  {/* Employee initials avatar */}
                  <div className="w-3.5 h-3.5 rounded-full bg-white/20 flex items-center justify-center mb-0.5 shrink-0">
                    <span className="text-3xs font-bold text-white/90 leading-none">
                      {shift.employee}
                    </span>
                  </div>
                  {/* Shift title */}
                  <span className="text-3xs font-semibold text-white leading-tight truncate">
                    {shift.title}
                  </span>
                  {/* Duration */}
                  <span className="text-3xs text-white/70 leading-tight">
                    {duration}h
                  </span>
                </div>
              );
            })}

            {/* Current time indicator — red line */}
            <div
              className="absolute left-0 right-0 z-10 pointer-events-none flex items-center"
              style={{ top: currentTimeTop }}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-red-500 -ml-[3px] shrink-0" />
              <div className="flex-1 h-[1.5px] bg-red-500" />
            </div>
          </div>
        </div>

        {/* Bottom gradient fade — suggests scrollability */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-card via-card/80 to-transparent pointer-events-none z-10" />
      </div>
    </div>
  );
};
